/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import React, { useCallback } from 'react';

// Mantine
import {
	Select as NativeSelect,
	Image,
	SelectProps,
} from '@mantine/core';

// Style
import classes from './Select.module.css';

// Icons
import ArrowDownIcon from '@assets/icons/arrow-down.svg';

// Utils
import { truncateString } from '@utils/functions';

// I18n
import { useTranslation } from 'react-i18next';

// Lodash
import isNil from 'lodash/isNil';
import { TSelectOption } from '@global-types/index';

// Type
type Item = {
	label: string;
	value: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Select = (
	props: SelectProps &
		any & {
			onChange: (_item: string) => void;
			size?: string;
			variant?: string;
			disabled?: boolean;
			leftSection?: React.ReactNode;
			data: Item[];
			visibleFrom?: string;
			hiddenFrom?: string;
			label?: string;
			withAsterisk?: boolean;
			placeholder?: string;
			defaultValue?: string;
			value?: string;
			error?: string;
			withoutrightsection?: boolean;
			notruncate?: boolean;
		}
) => {
	const { data, ...restProps } = props;

	const { t } = useTranslation();

	const updateOptions = useCallback(
		(options: TSelectOption[]) =>
			options.reduce(
				(
					acc: TSelectOption[],
					currentItem: TSelectOption
				) => {
					const modifiedItem = {
						...currentItem,
						label: !isNil(props.notruncate)
							? t(currentItem.label)
							: truncateString(t(currentItem.label), 24),
					};
					acc.push(modifiedItem);
					return acc;
				},
				[]
			),
		[data]
	);

	return (
		<NativeSelect
			classNames={classes}
			withCheckIcon={false}
			checkIconPosition="left"
			rightSectionPointerEvents="none"
			withScrollArea={false}
			searchable
			nothingFoundMessage="Nothing found..."
			rightSection={
				<Image
					className={classes.arrowDown}
					src={
						props.withoutrightsection
							? null
							: ArrowDownIcon
					}
				/>
			}
			data={updateOptions(data)}
			{...restProps}
		/>
	);
};
